import { InterventionTag } from '@/types';
import { useCallback } from 'react';
import {
  Icon,
  Inline,
  InlineItem,
  LabelText,
  Menu,
  MenuItem,
  Stack,
  Text,
} from '@kinesis/bungle';
import { tagOptions } from '@/components/tag-select';
import { interventionTags } from '@/data/interventions';
import { CheckSlot } from './tag-set-select.styles';

// The tag-set control for new-model pathway interventions: the intervention
// states outright which evaluations (Current / Intermediate / Aggressive) it
// applies in — one toggle row per evaluation, the check beside the label so
// the set reads at a glance at any width. Any subset is expressible (e.g.
// Intermediate alone — impossible in legacy); the legacy level-and-above
// gesture lives at creation (a new intervention starts on every evaluation),
// not here.
const TagSetSelect = (props: TagSetSelectProps) => {
  const { value, onChange } = props;

  const toggle = useCallback(
    (tag: InterventionTag) =>
      onChange(
        // Keep the set in canonical (ambition) order regardless of the
        // toggling order.
        interventionTags.filter((candidate) =>
          candidate === tag
            ? !value.includes(candidate)
            : value.includes(candidate),
        ),
      ),
    [value, onChange],
  );

  return (
    <Stack space='xsmall'>
      <LabelText>Applies in</LabelText>
      <Menu>
        {tagOptions.map((option) => (
          <MenuItem
            key={option.value}
            onClick={() => toggle(option.value as InterventionTag)}
            content={
              <Inline space='small'>
                <InlineItem sizing='fit-content'>
                  <CheckSlot>
                    {value.includes(option.value as InterventionTag) && (
                      <Icon magnitude='small' type='check' />
                    )}
                  </CheckSlot>
                </InlineItem>
                <InlineItem sizing='fit-content'>
                  <Icon color={option.color} magnitude='small' type='circle' />
                </InlineItem>
                <InlineItem sizing='fill-container'>
                  <Text>{option.label}</Text>
                </InlineItem>
              </Inline>
            }
          />
        ))}
      </Menu>
    </Stack>
  );
};

export type TagSetSelectProps = {
  value: InterventionTag[];
  onChange: (v: InterventionTag[]) => void;
};

export { TagSetSelect };
